import { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useAuth } from "../../auth/AuthContext";
import { MainCard } from "../../components/MainCard";
import { useEditorTree } from "../../lib/manifestEditor/useEditorTree";
import { EditorTree } from "../../lib/manifestEditor/EditorTree";
import { SelectedNode } from "../../lib/manifestEditor/SelectedNode";
import { CatalogPanel } from "../../lib/manifestEditor/panels/CatalogPanel";
import { PackPanel } from "../../lib/manifestEditor/panels/PackPanel";
import { LessonPanel } from "../../lib/manifestEditor/panels/LessonPanel";
import { EntryPanel } from "../../lib/manifestEditor/panels/EntryPanel";
import { FilePickerDialog } from "../../lib/manifestEditor/FilePickerDialog";

interface EntryLike {
  kind: "pack" | "catalog";
  id: string;
  title: string;
  [key: string]: unknown;
}
interface LessonLike {
  id: string;
  title: string;
  type: string;
  file: string;
  [key: string]: unknown;
}

function arrayAt<T>(value: Record<string, unknown>, key: string): T[] {
  return Array.isArray(value[key]) ? (value[key] as T[]) : [];
}
function withArray<T>(value: Record<string, unknown>, key: string, next: T[]): Record<string, unknown> {
  return { ...value, [key]: next };
}
function replaceAt<T>(list: T[], index: number, next: T): T[] {
  return list.map((item, i) => (i === index ? next : item));
}
function removeAt<T>(list: T[], index: number): T[] {
  return list.filter((_, i) => i !== index);
}
function swap<T>(list: T[], a: number, b: number): T[] {
  if (b < 0 || b >= list.length) return list;
  const next = [...list];
  [next[a], next[b]] = [next[b], next[a]];
  return next;
}

/**
 * The full-page manifest/catalog EDITOR (docs/343-ADMIN-MANIFEST-BUILDER-REDESIGN.md
 * v0) — a tree of Catalogs → Packs → Lessons (left) + a properties panel for
 * whichever node is selected (right), superseding the old single-form "Build
 * manifest" page.
 */
export function ManifestEditorPage() {
  const { client, token, backendUrl } = useAuth();
  const tree = useEditorTree(client, token, backendUrl);
  const [selected, setSelected] = useState<SelectedNode | null>(null);
  const [saving, setSaving] = useState<string | null>(null); // the doc key currently saving
  const [saveErrors, setSaveErrors] = useState<Record<string, string>>({});
  const [filePickerFor, setFilePickerFor] = useState<{ packKey: string; fieldKey: string } | null>(null);

  const saveDoc = async (key: string) => {
    setSaving(key);
    setSaveErrors((e) => ({ ...e, [key]: "" }));
    const result = await tree.save(key);
    setSaving(null);
    if (!result.ok) {
      setSaveErrors((e) => ({ ...e, [key]: result.error }));
      return;
    }
    if (result.newKey !== key) {
      // A brand-new draft just got its real id — follow the selection to it.
      setSelected((cur) => {
        if (!cur) return cur;
        if (cur.type === "catalog" && cur.key === key) return { type: "catalog", key: result.newKey };
        if (cur.type === "pack" && cur.key === key) return { type: "pack", key: result.newKey };
        if (cur.type === "lesson" && cur.packKey === key) return { ...cur, packKey: result.newKey };
        return cur;
      });
    }
  };

  const isDirty = (key: string) => {
    const d = tree.docs[key];
    if (!d) return false;
    if (d.saved === null) return true; // an unsaved new draft always counts as dirty
    return JSON.stringify(d.draft) !== JSON.stringify(d.saved);
  };

  const renderPanel = () => {
    if (!selected) {
      return (
        <Typography color="text.secondary">
          Select a catalog, pack, or lesson on the left — or create a new one.
        </Typography>
      );
    }

    if (selected.type === "catalog") {
      const doc = tree.docs[selected.key];
      if (!doc) return <Typography color="text.secondary">Loading…</Typography>;
      const entries = arrayAt<EntryLike>(doc.draft, "entries");
      return (
        <CatalogPanel
          value={doc.draft}
          onChange={(next) => tree.updateDraft(selected.key, next)}
          onSave={() => saveDoc(selected.key)}
          onDiscard={() => tree.discard(selected.key)}
          dirty={isDirty(selected.key)}
          saving={saving === selected.key}
          saveError={saveErrors[selected.key] || null}
          onAddPackEntry={() => {
            const next = withArray(doc.draft, "entries", [
              ...entries,
              { kind: "pack", id: "", title: "", manifestUrl: "" },
            ]);
            tree.updateDraft(selected.key, next);
            setSelected({ type: "packEntry", catalogKey: selected.key, index: entries.length });
          }}
          onAddCatalogEntry={() => {
            const next = withArray(doc.draft, "entries", [
              ...entries,
              { kind: "catalog", id: "", title: "", url: "" },
            ]);
            tree.updateDraft(selected.key, next);
            setSelected({ type: "catalogEntry", catalogKey: selected.key, index: entries.length });
          }}
        />
      );
    }

    if (selected.type === "pack") {
      const doc = tree.docs[selected.key];
      if (!doc) return <Typography color="text.secondary">Loading…</Typography>;
      if (doc.loading) return <Typography color="text.secondary">Loading…</Typography>;
      if (doc.loadError) return <Typography color="error">{doc.loadError}</Typography>;
      const lessons = arrayAt<LessonLike>(doc.draft, "lessons");
      return (
        <PackPanel
          value={doc.draft}
          onChange={(next) => tree.updateDraft(selected.key, next)}
          onSave={() => saveDoc(selected.key)}
          onDiscard={() => tree.discard(selected.key)}
          dirty={isDirty(selected.key)}
          saving={saving === selected.key}
          saveError={saveErrors[selected.key] || null}
          onAddLesson={() => {
            const next = withArray(doc.draft, "lessons", [
              ...lessons,
              { id: "", title: "", type: "reading", file: "", optional: false },
            ]);
            tree.updateDraft(selected.key, next);
            setSelected({ type: "lesson", packKey: selected.key, index: lessons.length });
          }}
        />
      );
    }

    if (selected.type === "lesson") {
      const doc = tree.docs[selected.packKey];
      if (!doc) return <Typography color="text.secondary">Loading…</Typography>;
      const lessons = arrayAt<LessonLike>(doc.draft, "lessons");
      const lesson = lessons[selected.index];
      if (!lesson) return <Typography color="text.secondary">This lesson was removed.</Typography>;
      return (
        <LessonPanel
          value={lesson}
          onChange={(next) =>
            tree.updateDraft(selected.packKey, withArray(doc.draft, "lessons", replaceAt(lessons, selected.index, next as LessonLike)))
          }
          onSave={() => saveDoc(selected.packKey)}
          onDiscard={() => tree.discard(selected.packKey)}
          dirty={isDirty(selected.packKey)}
          saving={saving === selected.packKey}
          saveError={saveErrors[selected.packKey] || null}
          onBrowseFile={(fieldKey) => setFilePickerFor({ packKey: selected.packKey, fieldKey })}
        />
      );
    }

    // packEntry / catalogEntry
    const doc = tree.docs[selected.catalogKey];
    if (!doc) return <Typography color="text.secondary">Loading…</Typography>;
    const entries = arrayAt<EntryLike>(doc.draft, "entries");
    const entry = entries[selected.index];
    if (!entry) return <Typography color="text.secondary">This entry was removed.</Typography>;
    return (
      <EntryPanel
        entry={entry as EntryLike & { kind: "pack" | "catalog" }}
        onChange={(next) =>
          tree.updateDraft(selected.catalogKey, withArray(doc.draft, "entries", replaceAt(entries, selected.index, next)))
        }
        onRemove={() => {
          tree.updateDraft(selected.catalogKey, withArray(doc.draft, "entries", removeAt(entries, selected.index)));
          setSelected({ type: "catalog", key: selected.catalogKey });
        }}
        onMoveUp={
          selected.index > 0
            ? () => {
                tree.updateDraft(selected.catalogKey, withArray(doc.draft, "entries", swap(entries, selected.index, selected.index - 1)));
                setSelected({ ...selected, index: selected.index - 1 });
              }
            : undefined
        }
        onMoveDown={
          selected.index < entries.length - 1
            ? () => {
                tree.updateDraft(selected.catalogKey, withArray(doc.draft, "entries", swap(entries, selected.index, selected.index + 1)));
                setSelected({ ...selected, index: selected.index + 1 });
              }
            : undefined
        }
        onOpenPack={(id) => {
          tree.loadPack(id);
          setSelected({ type: "pack", key: `manifest:${id}` });
        }}
        onOpenCatalog={(id) => setSelected({ type: "catalog", key: `catalog:${id}` })}
        backendUrl={tree.backendUrl}
        manifestIndex={tree.manifestIndex}
        catalogIndex={tree.catalogIndex}
      />
    );
  };

  return (
    <MainCard title="Manifest editor" contentSx={{ p: 0 }}>
      <Box sx={{ display: "flex", minHeight: 500 }}>
        <EditorTree
          tree={tree}
          selected={selected}
          onSelect={setSelected}
          onCreateCatalog={() => setSelected({ type: "catalog", key: tree.createCatalog() })}
          onCreatePack={() => setSelected({ type: "pack", key: tree.createPack() })}
        />
        <Box sx={{ flex: 1, p: 3, overflowY: "auto" }}>{renderPanel()}</Box>
      </Box>
      <FilePickerDialog
        open={!!filePickerFor}
        onClose={() => setFilePickerFor(null)}
        client={client}
        token={token}
        packId={filePickerFor ? tree.docs[filePickerFor.packKey]?.id || "" : ""}
        onPick={(relativeFile) => {
          if (!filePickerFor || !selected || selected.type !== "lesson") return;
          const doc = tree.docs[filePickerFor.packKey];
          if (!doc) return;
          const lessons = arrayAt<LessonLike>(doc.draft, "lessons");
          const lesson = lessons[selected.index];
          if (!lesson) return;
          const nextLesson = { ...lesson, [filePickerFor.fieldKey]: relativeFile };
          tree.updateDraft(filePickerFor.packKey, withArray(doc.draft, "lessons", replaceAt(lessons, selected.index, nextLesson)));
        }}
      />
    </MainCard>
  );
}
